import React from "react";

import { Link } from "@/i18n/routing";
import Loader from "@/icons/ui/feedback/spinner.svg";
import ArrowDown from "@/icons/ui/navigation/arrow-down.svg";
import { cn } from "@/utils/cn";

import styles from "./style.module.scss";

/**
 * @deprecated Use the new Button component from @/ui/Button instead.
 * This component is deprecated and will be removed in the future.
 */

type Variant =
  | "white"
  | "red"
  | "gray"
  | "selector"
  | "grayText"
  | "selectedPrice"
  | "nonSelectedPrice";

const getButtonClass = (type: Variant = "red") => {
  switch (type) {
    case "white":
      return styles.white;
    case "red":
      return styles.red;
    case "gray":
      return styles.gray;
    case "grayText":
      return styles.grayText;
    case "selector":
      return styles.selector;
    case "selectedPrice":
      return styles.selectedPrice;
    case "nonSelectedPrice":
      return styles.nonSelectedPrice;
    default:
      return styles.violet;
  }
};

interface ButtonProps {
  link?: string;
  href?: string;
  target?: string;
  type?: Variant;
  htmlType?: "button" | "submit" | "reset";
  disabled?: boolean;
  busy?: boolean;
  selectorArrow?: boolean;
  arrowColor?: "gray" | "white";
  children?: React.ReactNode;
  className?: string;
  onClick?: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const ButtonContent = ({
  busy,
  selectorArrow,
  arrowColor,
  type,
  children,
}: {
  busy: boolean;
  selectorArrow?: boolean;
  arrowColor?: "gray" | "white";
  type: Variant;
  children: React.ReactNode;
}) => {
  if (busy) {
    return (
      <div className={styles.loadingContainer}>
        <Loader className={styles.loaderIcon} />
      </div>
    );
  }

  if (selectorArrow) {
    return (
      <span>
        {children}
        <ArrowDown
          alt="arrow_down"
          className={cn(
            styles.arrow_down,
            arrowColor === "white" ? styles.arrowWhite : styles.arrowGray,
          )}
        />
      </span>
    );
  }

  const selectedPriceContent =
    type === "selectedPrice" || type === "nonSelectedPrice" ? (
      <div className={styles.selected_square_new}>
        <div className={styles.selected_square_inner}></div>
      </div>
    ) : null;

  const selectedArrow =
    type === "selectedPrice" ? (
      <ArrowDown className={styles.selected_arrow_new} />
    ) : null;
  const result = selectedPriceContent ? (
    <div className={styles.button_content_wrapper}>
      {selectedPriceContent}
      <span>{children}</span>
    </div>
  ) : (
    <span>{children}</span>
  );
  return result;
};

export default function Button(props: ButtonProps) {
  const {
    disabled = false,
    type = "red",
    htmlType = "button",
    busy = false,
    arrowColor = "white",
    link,
    target,
    href,
    children,
    onClick,
    className,
    selectorArrow,
  } = props;

  const buttonClass = cn(
    styles.button,
    styles.variant,
    getButtonClass(type),
    className,
    type === "selector" && styles.grayBackground,
    // selectorArrow && styles.selectorArrow,
  );

  const buttonContent = (
    <ButtonContent
      busy={busy}
      selectorArrow={selectorArrow}
      arrowColor={arrowColor}
      type={type}
    >
      {children}
    </ButtonContent>
  );

  if (link) {
    return (
      <Link
        href={disabled || busy ? "#" : link}
        className={buttonClass}
      >
        {buttonContent}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={disabled || busy ? "#" : href}
        target={target}
        className={buttonClass}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <button
      type={htmlType}
      onClick={onClick}
      className={buttonClass}
      disabled={disabled || busy}
    >
      {buttonContent}
    </button>
  );
}
